/** Formatting helpers for per-scenario resource caps (CAO-202): max RPS,
 * max bandwidth (stored normalized to bits/second), and max PPS. All three
 * are per-bot and enforced by the bot runtime; null/undefined = unlimited.
 */

type BandwidthUnit = 'bps' | 'Kbps' | 'Mbps' | 'Gbps';

const BANDWIDTH_UNIT_MULTIPLIERS: Record<BandwidthUnit, number> = {
  bps: 1,
  Kbps: 1_000,
  Mbps: 1_000_000,
  Gbps: 1_000_000_000,
};

const formatNumber = (value: number): string => {
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 100) / 100;
  return rounded.toLocaleString();
};

/** Format a plain rate (RPS/PPS) with a unit suffix, e.g. "1,500 req/s". */
export const formatRate = (value: number, suffix: string): string => `${formatNumber(value)} ${suffix}`;

/** Format a bits/second value using the largest unit that keeps it readable, e.g. "250 Mbps". */
export const formatBandwidth = (bps: number): string => {
  const units: BandwidthUnit[] = ['Gbps', 'Mbps', 'Kbps', 'bps'];
  for (const unit of units) {
    const scaled = bps / BANDWIDTH_UNIT_MULTIPLIERS[unit];
    if (scaled >= 1) return formatNumber(scaled) + ' ' + unit;
  }
  return formatNumber(bps) + ' bps';
};

export interface ResourceCaps {
  max_rps?: number | null;
  max_bandwidth_bps?: number | null;
  max_pps?: number | null;
}

/** True when at least one cap is configured (not unlimited). */
export const hasResourceCaps = (caps: ResourceCaps | null | undefined): boolean =>
  !!caps && (caps.max_rps != null || caps.max_bandwidth_bps != null || caps.max_pps != null);

/** One line per configured cap: "<per-bot value>" or, when botCount > 1, "<per-bot> (~<aggregate> across N bots)". */
export const describeResourceCaps = (caps: ResourceCaps | null | undefined, botCount?: number): string[] => {
  if (!caps) return [];
  const bots = botCount && botCount > 1 ? botCount : null;
  const lines: string[] = [];

  if (caps.max_rps != null) {
    const perBot = formatRate(caps.max_rps, 'req/s per bot');
    lines.push(bots ? `${perBot} (~${formatRate(caps.max_rps * bots, 'req/s')} aggregate across ${bots} bots)` : perBot);
  }
  if (caps.max_bandwidth_bps != null) {
    const perBot = `${formatBandwidth(caps.max_bandwidth_bps)} per bot`;
    lines.push(bots ? `${perBot} (~${formatBandwidth(caps.max_bandwidth_bps * bots)} aggregate across ${bots} bots)` : perBot);
  }
  if (caps.max_pps != null) {
    const perBot = formatRate(caps.max_pps, 'pps per bot');
    lines.push(bots ? `${perBot} (~${formatRate(caps.max_pps * bots, 'pps')} aggregate across ${bots} bots)` : perBot);
  }
  return lines;
};
